<template>
  <ContestEntryFormLayout
    :load-state="loadState"
    :error-message="t('contestEntryForm.step1.loadError')"
    :current-step="1"
    :title="t('contestEntryForm.step1.title')"
    :description="t('contestEntryForm.step1.description')"
    :contest="microCmsContest"
  >
    <ContestEntryApplicantInfoForm v-model="form" :penname-options="pennameOptions" @add-penname="onAddPenname" />

    <div class="form-footer">
      <button type="button" class="next-button" :disabled="!canProceed" @click="onClickNext">
        {{ t("contestEntryForm.step1.next") }}
      </button>
    </div>
  </ContestEntryFormLayout>
</template>

<script lang="ts">
import { NavigationGuardNext, Route } from "vue-router";
import { resolveTermsAgreement } from "@/lib/utils/contestEntryFormHistory";

export default {
  metaInfo: {
    meta: [{ name: "robots", content: "none" }],
  },
  beforeRouteEnter(to: Route, _from: Route, next: NavigationGuardNext) {
    const { id } = to.params;
    if (typeof id === "string" && id && !resolveTermsAgreement(id)) {
      next({
        name: "eventContestTerms",
        params: { id },
        replace: true,
      });
      return;
    }
    next();
  },
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { Auth } from "aws-amplify";
import { z } from "zod";
import { NolaPfContestClient } from "@/lib/clients";
import { User } from "@/lib/models";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import {
  CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH,
  CONTEST_ENTRY_PENNAME_MAX_LENGTH,
} from "@/lib/utils/contestEntryApplicantForm";
import { buildDisplayedPennames } from "@/lib/utils/pennameList";
import { pushWithContestEntryForms, readApplicantFormFromHistoryState } from "@/lib/utils/contestEntryFormHistory";
import ContestEntryFormLayout from "@/components/templates/ContestEntryFormLayout.vue";
import ContestEntryApplicantInfoForm, {
  ContestEntryApplicantFormValue,
} from "@/components/organisms/event/ContestEntryApplicantInfoForm.vue";

type LoadState = "loading" | "loaded" | "error";

const nolaPfContestClient = new NolaPfContestClient();

const NAME_KANA_REGEXP = /^[\u3040-\u309F\u30FC\s]+$/;
const PHONE_REGEXP = /^0\d{9,10}$/;

const instance = getCurrentInstance();
const { t } = useI18n();

const loadState = ref<LoadState>("loading");
const microCmsContest = ref<NolaPfMicroCmsContest | null>(null);
const pennameOptions = ref<string[]>([]);

const emptyForm = (): ContestEntryApplicantFormValue => ({
  name: "",
  nameKana: "",
  penname: "",
  email: "",
  phone: "",
  gender: 0,
  birthDate: "",
  historyOfAwards: "",
});

const form = ref<ContestEntryApplicantFormValue>(emptyForm());

const contestId = computed(() => instance?.proxy.$route.params.id as string);

function formatPhone(phone?: string): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function toDateInputValue(birthDate?: string): string {
  if (!birthDate) return "";
  const matched = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!matched) return "";
  return `${matched[1]}-${matched[2]}-${matched[3]}`;
}

function toPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

const canProceed = computed(() => {
  const schema = z.object({
    name: z.string().max(100).min(1),
    nameKana: z
      .string()
      .max(100)
      .min(1)
      .refine((value) => NAME_KANA_REGEXP.test(value)),
    penname: z.string().max(CONTEST_ENTRY_PENNAME_MAX_LENGTH).min(1),
    email: z.string().email().max(100),
    phone: z
      .string()
      .max(100)
      .refine((value) => PHONE_REGEXP.test(toPhoneDigits(value))),
    birthDate: z.string().min(1),
    historyOfAwards: z.string().max(CONTEST_ENTRY_HISTORY_OF_AWARDS_MAX_LENGTH),
  });

  return schema.safeParse({
    ...form.value,
    phone: toPhoneDigits(form.value.phone),
  }).success;
});

function appendPennameOption(options: string[], penname: string): string[] {
  if (!penname || options.includes(penname)) return options;
  return [...options, penname];
}

function onAddPenname(name: string) {
  pennameOptions.value = appendPennameOption(pennameOptions.value, name);
  form.value = { ...form.value, penname: name };
}

function applyProfileToForm(
  profile: Awaited<ReturnType<NolaPfContestClient["fetchProfile"]>>,
  nolaPenname: string,
  email: string
) {
  const displayedPennames = buildDisplayedPennames(nolaPenname, profile?.pennames ?? []);
  pennameOptions.value = displayedPennames;

  form.value = {
    name: profile?.name ?? "",
    nameKana: profile?.nameKana ?? "",
    penname: displayedPennames[0] ?? "",
    email,
    phone: formatPhone(profile?.phone),
    gender: profile?.gender ?? 0,
    birthDate: toDateInputValue(profile?.birthDate),
    historyOfAwards: profile?.historyOfAwards ?? "",
  };
}

async function loadPageData() {
  const id = contestId.value;
  if (!id) {
    loadState.value = "error";
    return;
  }

  loadState.value = "loading";

  try {
    const profilePromise = nolaPfContestClient.fetchProfile();
    const userPromise = instance!.proxy.$store.dispatch("userModule/initialize");
    const microCmsContestPromise = nolaPfContestClient.fetchMicroCmsContest(id);

    const [profile, , microCmsContestResult] = await Promise.all([profilePromise, userPromise, microCmsContestPromise]);

    microCmsContest.value = microCmsContestResult;

    const user = instance!.proxy.$store.getters["userModule/user"] as User | undefined;
    if (!user?.userId) {
      throw new Error("Failed to fetch user");
    }

    let email = "";
    try {
      const cognitoUser = await Auth.currentUserPoolUser();
      email = cognitoUser.attributes.email ?? "";
    } catch {
      // Cognito 未ログイン等は空のまま
    }

    const savedForm = readApplicantFormFromHistoryState(id);
    if (savedForm) {
      const displayedPennames = buildDisplayedPennames(user.name ?? "", profile?.pennames ?? []);
      pennameOptions.value = appendPennameOption(displayedPennames, savedForm.penname);
      form.value = savedForm;
    } else {
      applyProfileToForm(profile, user.name ?? "", email);
    }
    loadState.value = "loaded";
  } catch (error) {
    console.error("Failed to load contest entry form step1:", error);
    loadState.value = "error";
  }
}

async function onClickNext() {
  if (!canProceed.value) return;

  const id = contestId.value;
  const applicantForm = { ...form.value };

  await pushWithContestEntryForms(
    (location) => instance!.proxy.$router.push(location),
    {
      name: "eventContestEntryFormStep2",
      params: { id },
    },
    id,
    { applicantForm }
  );
}

onMounted(() => {
  loadPageData();
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.form-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
}

.next-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }
}

@include mq("sp") {
  .form-footer {
    margin-top: 24px;
  }
}
</style>
